import React from 'react';
import PropTypes from 'prop-types';

import Loader from 'relements/build/esm/decorators/loader';
import Text from 'relements/build/esm/ui/text';

import connection from '@src/connection';
import {
  GRAPH_NODE_TYPE,
  GRAPH_NODE_DISPLAY_TEXT,
  FEATURE_FLAG_DATA,
} from 'constants/MogamboConstants';

import { updateABTestingDrawerFlag, setRHSActive } from 'actions/BotActions';
import CommonStore from '@src/stores/CommonStore';

import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import BotStore from '@src/store';

import styled from 'styled-components';

import GraphView from './GraphView';

import GraphEmptyState from './GraphEmptyState';
import * as maps from './maps';
import styles from './Graph.scss';

import { GOOGLE_ANALYTICS } from '../../../const';

export const StyledCenterText = styled(Text)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

/**
 * The Main Graph Container
 * Loads the main graph component with the right props and callbacks
 * Also responsible for showing the empty state if there are no substories
 * or the loader if the graph request is still not complete
 * @type {[type]}
 */
@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
class Graph extends React.PureComponent {
  /**
   * On mounting,
   * We add the graph change listener so that we can force update
   * the graph in case we receive a particular change event.
   *
   * We also add a loader if the data for the graph hasn't been returned yet
   * @return {[type]} [description]
   */
  componentDidMount() {
    maps.addGraphUpdateListener(this._updateGraph);
    maps.addGraphShallowUpdateListener(this._updateGraphShallow);
    if (!this.props.graphLoading) {
      this.props.deactivateLoader();
    } else {
      this.props.activateLoader();
    }
  }

  /**
   * On unmount we remove any listeners we might've added to prevent leaks
   * @return {[type]} [description]
   */
  componentWillUnmount() {
    maps.removeOnGraphUpdateListener(this._updateGraph);
    maps.removeGraphShallowUpdateListener(this._updateGraphShallow);
  }

  /**
   * If the hadn't loaded, and has loaded this tick, we deaactivate the loader
   * @param  {[type]} nextProps [description]
   * @return {[type]}           [description]
   */
  componentWillReceiveProps(nextProps) {
    if (this.props.graphLoading && !nextProps.graphLoading) {
      this.props.deactivateLoader();
    }
  }

  /**
   * Render
   * @return {[type]} [description]
   */
  render() {
    const { graph, showEmptyStateIndicator } = this.props;
    const numSteps = (graph || []).reduce(
      (s, subStory) => s + subStory.nodeList.length,
      0
    );
    const locationQuery = new URLSearchParams(
      this.props.router.location.search
    );
    const activeConnection = locationQuery.fromId;

    if ((graph && graph.length === 0) || (graph && numSteps === 0)) {
      return (
        <div className={styles.graph} id="graph-wrapper">
          <GraphEmptyState
            setDroppable={this.props.setDroppable}
            showEmptyStateIndicator={showEmptyStateIndicator}
          />
        </div>
      );
    }

    const connection = {
      source: this.props.router.match.params.sourceId,
      target: this.props.router.match.params.destId,
    };
    return (
      <div className={styles.graph} id="graph-wrapper">
        {graph ? (
          <GraphView
            ref={(graph) => {
              this._graph = graph;
            }}
            domain={this.props.domain}
            story={this.props.story}
            subStory={this.props.subStory}
            step={this.props.step}
            connection={connection}
            graph={this.props.graph}
            allSteps={this.props.allSteps}
            onNodeClick={this._navToStep}
            onConnectionClick={this._navToConnection}
            activeConnection={activeConnection}
            updateConnections={this._updateConnections}
            setDroppable={this.props.setDroppable}
            showGrid={false}
            defaultCenter={false}
            panOnClick={false}
            currentLanguage={this.props.currentLanguage}
            onNewNode={this.props.onNewNode}
            nodeContextMenuEnabled={this.props.nodeContextMenuEnabled}
          />
        ) : null}
      </div>
    );
  }

  _updateConnections = () => {
    if (this.props.step && this.props.step.id) {
      maps.setStepConnections(this.props.step.id);
    }
  };

  /**
   * Fetches the graph from the backend (API call) to get the latest data
   * and then the graph is force updated through it's ref.
   * @return {[type]} [description]
   */
  _updateGraph = (e) => {
    if (!this._graph) return;
    this._graph.update(e);
  };

  /**
   * A pure force update of the graph itself
   * @return {[type]} [description]
   */
  _updateGraphShallow = () => {
    if (!this._graph) return;
    this._graph.forceUpdate();
  };

  /**
   * This is the onClick handler for the nodes drawn by the Graph
   * on clicking the node, we should navigate to the right URL and we should
   * open the node navigation.
   * @param  {[type]} nodeId        [description]
   * @param  {[type]} nodeTitle     [description]
   * @param  {[type]} subStoryId    [description]
   * @param  {[type]} subStoryTitle [description]
   * @return {[type]}               [description]
   */
  _navToStep = (nodeId, nodeTitle, subStoryId, type) => {
    /**
     * if the node is new, we give it a name of 'New Node'
     */
    const { meta } = this.props;
    if (nodeId === meta.step.id && subStoryId === meta.subStory.id) {
      // if the id has not changed, no need to continue
      return;
    }

    const newMeta = {
      ...meta,
      step: { ...meta.step, id: nodeId },
      subStory: { ...meta.subStory, id: subStoryId },
    };

    const isNew = nodeId.startsWith('new');

    switch (type) {
      case GRAPH_NODE_DISPLAY_TEXT.STATIC:
        if (isNew) {
          maps.navToNewStep(newMeta, nodeId);
        } else {
          maps.navToStep(newMeta, nodeId, subStoryId);
        }

        break;

      case GRAPH_NODE_DISPLAY_TEXT.OUTPUT:
        maps.navToDynamicStep(newMeta, 'bot-says');
        break;

      case GRAPH_NODE_DISPLAY_TEXT.CODE:
        maps.navToCodeStep(newMeta, 'output');
        break;

      default:
        break;
    }

    /**
     * if the node is new, we go to the new step route instead of the default
     * node route
     */
    const title = isNew ? 'New Step' : nodeTitle;
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.NODE,
      GOOGLE_ANALYTICS.EVENTS.NODE_OPEN,
      `node-${title}`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  };

  /**
   * On clicking a specific connection (link) we open the connections page
   * @param  {[type]} sourceNode      [description]
   * @param  {[type]} destinationNode [description]
   * @return {[type]}                 [description]
   */
  _navToConnection = (sourceNode, destinationNode) => {
    let sourceNodeType;
    let destNodeType;

    if (sourceNode.type === GRAPH_NODE_DISPLAY_TEXT.STATIC) {
      sourceNodeType = GRAPH_NODE_TYPE.STATIC;
    } else if (sourceNode.type === GRAPH_NODE_DISPLAY_TEXT.CODE) {
      sourceNodeType = GRAPH_NODE_TYPE.CODE;
    } else if (sourceNode.type === GRAPH_NODE_DISPLAY_TEXT.OUTPUT) {
      sourceNodeType = GRAPH_NODE_TYPE.OUTPUT;
    } else if (sourceNode.type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE) {
      sourceNodeType = GRAPH_NODE_TYPE.AB_NODE;
    }

    if (destinationNode.type === GRAPH_NODE_DISPLAY_TEXT.STATIC) {
      destNodeType = GRAPH_NODE_TYPE.STATIC;
    } else if (destinationNode.type === GRAPH_NODE_DISPLAY_TEXT.CODE) {
      destNodeType = GRAPH_NODE_TYPE.CODE;
    } else if (destinationNode.type === GRAPH_NODE_DISPLAY_TEXT.OUTPUT) {
      destNodeType = GRAPH_NODE_TYPE.OUTPUT;
    } else if (destinationNode.type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE) {
      destNodeType = GRAPH_NODE_TYPE.AB_NODE;
    }

    if (
      CommonStore.getABTestingDrawerFlag() &&
      destinationNode.type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE
    ) {
      setRHSActive(false);
      updateABTestingDrawerFlag(false);
    }

    maps.fetchGraph(this.props.meta.story.id, () => {
      maps.navToConnectionSubMenu(
        {
          ...this.props.meta,
          step: {
            id: destinationNode.stepId,
            title: destinationNode.title,
          },
          subStory: { id: destinationNode.subStoryId },
          connection: {
            sourceType: sourceNodeType,
            sourceId: sourceNode.stepId,
            destType: destNodeType,
            destId: destinationNode.stepId,
          },
        },
        'user-says'
      );
    });
  };
}

Graph.propTypes = {
  graph: PropTypes.arrayOf(PropTypes.object),
  allSteps: PropTypes.arrayOf(PropTypes.object),
  meta: PropTypes.object,
  domain: PropTypes.object,
  story: PropTypes.object,
  subStory: PropTypes.object,
  step: PropTypes.object,
  router: PropTypes.object,
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
};

Graph.defaultProps = {
  meta: {},
  domain: {},
  story: {},
  subStory: {},
  step: {},
  router: {},
  activateLoader: () => {},
  deactivateLoader: () => {},
};

export default Graph;
